import { FormHelperText, Grid2, IconButton, TextFieldProps, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import Dropzone from "../../features/FileDropzone/Dropzone.component";
import { FileObject } from "../../features/FileDropzone/typings/FileObject.type";

type P = {
  files?: FileObject[];
  /** Current uploaded image */
  current?: string;
  title?: string;
  preview?: boolean;
  acceptedFileTypes?: Record<string, string[]>;
  maxFiles?: number;
  /** Action when File is added (through drop or click) */
  onAdd?: (newFiles: FileObject[]) => void;
  /** Action when File is deleted */
  onDelete?: (deletedFileObject: FileObject, index: number) => void;
  handleClose?: () => void;
  style?: {
    titleTypography?: string;
    parentMarginBottom?: number;
    height?: string;
  };
} & Pick<TextFieldProps, "error" | "helperText">;

/**
 * Controlled Dropzone for pictures with customized snackbar notifications.
 * @description Notifications are shown in main app snackbar. Shows current file on server in preview.
 */
export const PictureDropzone2: React.FC<P> = ({
  files = [],
  onAdd,
  onDelete,
  title = i18n.t("Upload an image"),
  current,
  error,
  helperText,
  preview = true,
  maxFiles = 1,
  handleClose,
  style = {
    titleTypography: "h6",
    parentMarginBottom: 2,
    height: "200px"
  }
}) => {
  const { t } = useTranslation();

  const acceptedImageTypes = {
    "image/jpg": [".jpg"],
    "image/jpeg": [".jpeg", ".jpg"],
    "image/png": [".png"],
    "image/gif": [".gif"]
  };

  return (
    <Grid2
      data-testid="picture-dropzone-parent"
      container
      direction="column"
      sx={{ marginBottom: style.parentMarginBottom }}
    >
      <Grid2 container data-testid="picture-dropzone-header">
        <Grid2 sx={{ textAlign: "left", alignSelf: "center" }} size={{ xs: 10 }}>
          <Typography variant={style.titleTypography as any}>{title}</Typography>
        </Grid2>
        {handleClose && (
          <Grid2 sx={{ textAlign: "right" }} size={{ xs: 2 }}>
            <IconButton data-testid="btn-close" onClick={handleClose} size="large">
              <Close />
            </IconButton>
          </Grid2>
        )}
      </Grid2>

      <Grid2 data-testid="picture-dropzone-base">
        <Dropzone
          files={files}
          dropzoneDesc={t("Drop an image or click to upload")}
          acceptedFileTypes={acceptedImageTypes}
          maxFiles={maxFiles}
          // maxFileSize={2097152} // 2MB in bytes for images
          onAdd={onAdd}
          onDelete={onDelete}
          containerHeight={style.height}
        />
      </Grid2>

      {error && helperText && <FormHelperText error>{helperText}</FormHelperText>}
      {current && current !== "temp" && preview && (
        <Grid2>
          <Typography sx={{ padding: "10px 0" }}>{t("Current :")}</Typography>
          <img src={current} height={110} alt="" />
        </Grid2>
      )}
    </Grid2>
  );
};
